

























































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import deck from '@/store/modules/deck';
import user from '@/store/modules/user';
import { ticGetInsights } from '@/store/api';
import CompanySelection from '@/components/CompanySelection.vue';
import {
  assignUniqueInsightImages,
  imageForInsight,
} from '@/components/simpleSlide/insightImages';

interface InsightQuestion {
  id: string;
  category?: string;
  question: string;
  slideQuestion?: string;
  image?: string;
}

@Component({
  components: { CompanySelection },
})
export default class AiInsightsEditor extends Vue {
  @Prop() readonly subSlide!: any;
  @Prop({ default: false }) readonly hideTitle!: boolean;

  company: any = { label: '', value: '' };
  loading: boolean = false;
  loadError: string = '';
  questions: InsightQuestion[] = [];

  readonly minSelected = 2;
  readonly maxSelected = 3;

  private insightsLoadSeq = 0;
  private insightsPollTimer: ReturnType<typeof setTimeout> | null = null;

  get orgNr(): string {
    const fromSlide = this.subSlide && this.subSlide.OrgNr;
    const fromDeck = deck.OrgNr;
    const value = fromSlide || fromDeck || '';
    if (!value || value === '00') {
      return '';
    }
    return value;
  }

  get hasCompany(): boolean {
    return !!this.orgNr;
  }

  get selectedIds(): string[] {
    if (!this.subSlide || !Array.isArray(this.subSlide.selectedInsightIds)) {
      return [];
    }
    return this.subSlide.selectedInsightIds;
  }

  get selectedCount(): number {
    return this.selectedIds.length;
  }

  get selectionHint(): string {
    if (this.selectedCount >= this.maxSelected) {
      return 'Max tre frågor kan visas. Avmarkera en fråga för att byta ut den.';
    }
    return 'Markera 2–3 frågor som ska visas på sidan. Du kan redigera presentationsversionen för valda frågor.';
  }

  get title(): string {
    return (this.subSlide && this.subSlide.title) || '';
  }

  set title(value: string) {
    if (!this.subSlide) {
      return;
    }
    this.subSlide.title = value;
  }

  mounted() {
    this.ensureDefaults();
    this.loadCompany();
    this.loadInsights();
  }

  beforeDestroy() {
    this.clearInsightsPoll();
  }

  @Watch('orgNr')
  onOrgNrChanged() {
    this.loadCompany();
    this.loadInsights();
  }

  ensureDefaults() {
    if (!this.subSlide) {
      return;
    }
    if ((!this.subSlide.OrgNr || this.subSlide.OrgNr === '00') && deck.OrgNr && deck.OrgNr !== '00') {
      this.subSlide.OrgNr = deck.OrgNr;
    }
    if (!this.subSlide.title) {
      this.subSlide.title = 'Insikter';
    }
    if (!Array.isArray(this.subSlide.selectedInsightIds)) {
      this.subSlide.selectedInsightIds = [];
    }
    if (!Array.isArray(this.subSlide.insights)) {
      this.subSlide.insights = [];
    }
  }

  async loadCompany() {
    if (!this.hasCompany) {
      this.company = { label: '', value: '' };
      return;
    }

    if (this.company && this.company.value === this.orgNr && this.company.label) {
      return;
    }

    if (deck.OrgNr === this.orgNr && deck.OrgName) {
      this.company = {
        value: this.orgNr,
        label: deck.OrgName,
      };
      return;
    }

    const c = await user.getCompany(this.orgNr);
    if (!c) {
      this.company = { label: '', value: this.orgNr };
      return;
    }

    this.company = {
      value: c.OrgNr,
      label: c.Name,
      dateTo: c.DateTo,
    };
  }

  onCompanyUpdated(company: any) {
    if (!company || !company.value) {
      return;
    }

    const previousOrgNr = this.orgNr;
    const nextOrgNr = company.value;

    this.company = company;

    if (nextOrgNr === deck.OrgNr) {
      delete this.subSlide.OrgNr;
    } else {
      this.subSlide.OrgNr = nextOrgNr;
    }

    if (previousOrgNr !== nextOrgNr) {
      this.subSlide.selectedInsightIds = [];
      this.subSlide.insights = [];
    }

    this.onUpdate();
    this.loadInsights();
  }

  clearInsightsPoll() {
    if (this.insightsPollTimer != null) {
      clearTimeout(this.insightsPollTimer);
      this.insightsPollTimer = null;
    }
  }

  scheduleInsightsPoll(requestOrgNr: string) {
    this.clearInsightsPoll();
    this.insightsPollTimer = setTimeout(() => {
      this.insightsPollTimer = null;
      if (this.orgNr === requestOrgNr) {
        this.loadInsights();
      }
    }, 2000);
  }

  normalizeQuestionText(text: string): string {
    return (text || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  getStoredInsightObjects(): InsightQuestion[] {
    if (!this.subSlide || !Array.isArray(this.subSlide.insights)) {
      return [];
    }
    return this.subSlide.insights.filter(
      (item: any) => item && typeof item !== 'string' && item.question,
    );
  }

  async loadInsights() {
    const requestOrgNr = this.orgNr;
    if (!requestOrgNr) {
      this.clearInsightsPoll();
      this.loadError = '';
      this.questions = [];
      this.loading = false;
      return;
    }

    this.clearInsightsPoll();
    const seq = ++this.insightsLoadSeq;
    this.loading = true;
    this.loadError = '';

    let keepLoading = false;
    try {
      const data = await ticGetInsights(requestOrgNr);
      if (seq !== this.insightsLoadSeq || this.orgNr !== requestOrgNr) {
        return;
      }

      if (data === undefined) {
        this.loadError = 'Kunde inte hämta AI-insikter. Försök igen senare.';
        this.questions = [];
        return;
      }

      if (data && data.generating === true) {
        keepLoading = true;
        this.questions = [];
        this.scheduleInsightsPoll(requestOrgNr);
        return;
      }

      if (!data || !Array.isArray(data.questions) || !data.questions.length) {
        this.questions = [];
        return;
      }

      this.questions = data.questions
        .filter((q: any) => q && q.question)
        .map((q: any) => ({
          id: String(q.id),
          category: q.category || '',
          question: q.question,
          slideQuestion: q.slide_question || q.slideQuestion || '',
        }));

      this.syncSelectionWithAvailable();
    } finally {
      if (seq === this.insightsLoadSeq && !keepLoading) {
        this.loading = false;
      }
    }
  }

  presentationText(item: InsightQuestion): string {
    if (item.slideQuestion != null && item.slideQuestion !== '') {
      return item.slideQuestion;
    }
    return item.question || '';
  }

  storedSlideText(q: InsightQuestion): string {
    const stored = this.findStoredInsight(q.id);
    if (stored) {
      return stored.slideQuestion != null && stored.slideQuestion !== ''
        ? stored.slideQuestion
        : this.presentationText(q);
    }
    return this.presentationText(q);
  }

  findStoredInsight(id: string): InsightQuestion | null {
    if (!this.subSlide || !Array.isArray(this.subSlide.insights)) {
      return null;
    }
    const found = this.subSlide.insights.find(
      (item: any) => item && typeof item !== 'string' && item.id === id,
    );
    return found || null;
  }

  existingSlideQuestionMap(): Map<string, string> {
    const map = new Map<string, string>();
    if (!this.subSlide || !Array.isArray(this.subSlide.insights)) {
      return map;
    }
    for (const item of this.subSlide.insights) {
      if (!item || typeof item === 'string' || !item.id) {
        continue;
      }
      if (item.slideQuestion != null && item.slideQuestion !== '') {
        map.set(item.id, item.slideQuestion);
      }
    }
    return map;
  }

  existingImageMap(): Map<string, string> {
    const map = new Map<string, string>();
    if (!this.subSlide || !Array.isArray(this.subSlide.insights)) {
      return map;
    }
    for (const item of this.subSlide.insights) {
      if (!item || typeof item === 'string' || !item.id || !item.image) {
        continue;
      }
      map.set(item.id, item.image);
    }
    return map;
  }

  buildStoredInsights(
    ids: string[],
    byId: Map<string, InsightQuestion>,
    preservedText?: Map<string, string>,
    preservedImages?: Map<string, string>,
  ): InsightQuestion[] {
    const preservedTextMap = preservedText || this.existingSlideQuestionMap();
    const preservedImagesMap = preservedImages || this.existingImageMap();
    const base = ids
      .map((id) => byId.get(id))
      .filter((item): item is InsightQuestion => !!item)
      .map((item) => {
        const stored = this.toStoredInsight(item);
        if (preservedTextMap.has(item.id)) {
          stored.slideQuestion = preservedTextMap.get(item.id)!;
        }
        return stored;
      });

    const keys = base.map(
      (item) => item.id || item.category || item.question || '',
    );
    const preferred = base.map(
      (item) => preservedImagesMap.get(item.id) || item.image,
    );
    const images = assignUniqueInsightImages(keys, preferred);
    return base.map((item, i) => ({
      ...item,
      image: images[i],
    }));
  }

  remappedPreservationMaps(
    stored: InsightQuestion[],
    ids: string[],
    byQuestionNorm: Map<string, InsightQuestion>,
  ): { textById: Map<string, string>; imageById: Map<string, string> } {
    const textById = this.existingSlideQuestionMap();
    const imageById = this.existingImageMap();
    const matchedIds = new Set(ids);

    for (const item of stored) {
      let targetId = item.id;
      if (!matchedIds.has(targetId) && item.question) {
        const rematched = byQuestionNorm.get(
          this.normalizeQuestionText(item.question),
        );
        if (rematched) {
          targetId = rematched.id;
        }
      }
      if (
        item.slideQuestion != null &&
        item.slideQuestion !== '' &&
        targetId
      ) {
        textById.set(targetId, item.slideQuestion);
      }
      if (item.image && targetId) {
        imageById.set(targetId, item.image);
      }
    }
    return { textById, imageById };
  }

  resolveSelectedIds(
    available: Map<string, InsightQuestion>,
    byQuestionNorm: Map<string, InsightQuestion>,
    stored: InsightQuestion[],
  ): string[] {
    const hasExistingInsights = stored.length > 0;
    const ids: string[] = [];
    const seen = new Set<string>();

    const addId = (id: string) => {
      if (seen.has(id) || !available.has(id)) {
        return;
      }
      seen.add(id);
      ids.push(id);
    };

    const rematchId = (storedItem: InsightQuestion): string | null => {
      const rematched = byQuestionNorm.get(
        this.normalizeQuestionText(storedItem.question),
      );
      return rematched ? rematched.id : null;
    };

    for (const id of this.selectedIds) {
      if (available.has(id)) {
        addId(id);
        continue;
      }
      const storedItem = stored.find((item) => item.id === id);
      if (storedItem) {
        const newId = rematchId(storedItem);
        if (newId) {
          addId(newId);
        }
      }
    }

    if (!ids.length && hasExistingInsights) {
      for (const item of stored) {
        if (available.has(item.id)) {
          addId(item.id);
        } else {
          const newId = rematchId(item);
          if (newId) {
            addId(newId);
          }
        }
      }
    }

    if (!ids.length && !hasExistingInsights && this.questions.length) {
      this.questions
        .slice(0, Math.min(this.minSelected, this.questions.length))
        .forEach((q) => addId(q.id));
    }

    return ids;
  }

  unmatchedStoredInsights(
    stored: InsightQuestion[],
    ids: string[],
    byQuestionNorm: Map<string, InsightQuestion>,
  ): InsightQuestion[] {
    const matchedIds = new Set(ids);
    return stored.filter((item) => {
      if (matchedIds.has(item.id)) {
        return false;
      }
      const rematched = byQuestionNorm.get(
        this.normalizeQuestionText(item.question),
      );
      if (rematched && matchedIds.has(rematched.id)) {
        return false;
      }
      return true;
    });
  }

  syncSelectionWithAvailable() {
    if (!this.subSlide) {
      return;
    }
    const available = new Map(
      this.questions.map((q) => [q.id, q] as [string, InsightQuestion]),
    );
    const byQuestionNorm = new Map(
      this.questions.map(
        (q) =>
          [this.normalizeQuestionText(q.question), q] as [
            string,
            InsightQuestion,
          ],
      ),
    );
    const stored = this.getStoredInsightObjects();
    const ids = this.resolveSelectedIds(available, byQuestionNorm, stored);
    const { textById, imageById } = this.remappedPreservationMaps(
      stored,
      ids,
      byQuestionNorm,
    );
    const matchedInsights = this.buildStoredInsights(
      ids,
      available,
      textById,
      imageById,
    );
    const unmatched = this.unmatchedStoredInsights(
      stored,
      ids,
      byQuestionNorm,
    );
    const nextInsights = matchedInsights.concat(unmatched);

    const changed =
      ids.length !== this.selectedIds.length ||
      ids.some((id, i) => this.selectedIds[i] !== id) ||
      !this.insightsMatch(nextInsights);
    if (changed) {
      this.subSlide.selectedInsightIds = ids;
      this.subSlide.insights = nextInsights;
      this.onUpdate();
    }
  }

  insightsMatch(nextInsights: InsightQuestion[]): boolean {
    const current = this.subSlide.insights;
    if (!Array.isArray(current) || current.length !== nextInsights.length) {
      return false;
    }
    for (let i = 0; i < nextInsights.length; i++) {
      const item = current[i];
      if (!item || typeof item === 'string') {
        return false;
      }
      if (
        item.id !== nextInsights[i].id ||
        item.question !== nextInsights[i].question ||
        item.slideQuestion !== nextInsights[i].slideQuestion ||
        item.image !== nextInsights[i].image
      ) {
        return false;
      }
    }
    return true;
  }

  toStoredInsight(item: InsightQuestion): InsightQuestion {
    const category = item.category || '';
    const slideQuestion = this.presentationText(item);
    return {
      id: item.id,
      category,
      question: item.question,
      slideQuestion,
      image: imageForInsight(item.id || category || item.question),
    };
  }

  isSelected(q: InsightQuestion): boolean {
    return this.selectedIds.indexOf(q.id) >= 0;
  }

  onCheckboxChange(q: InsightQuestion, event: Event) {
    const target = event.target as HTMLInputElement;
    const shouldSelect = target.checked;
    if (shouldSelect === this.isSelected(q)) {
      return;
    }
    this.toggleQuestion(q);
    // Keep checkbox in sync if toggle was rejected (min/max)
    this.$nextTick(() => {
      target.checked = this.isSelected(q);
    });
  }

  toggleQuestion(q: InsightQuestion) {
    if (!this.subSlide) {
      return;
    }
    if (!Array.isArray(this.subSlide.selectedInsightIds)) {
      this.subSlide.selectedInsightIds = [];
    }

    const ids: string[] = this.subSlide.selectedInsightIds.slice();
    const index = ids.indexOf(q.id);
    if (index >= 0) {
      if (ids.length <= this.minSelected) {
        return;
      }
      ids.splice(index, 1);
    } else {
      if (ids.length >= this.maxSelected) {
        return;
      }
      ids.push(q.id);
    }

    const byId = new Map(this.questions.map((item) => [item.id, item]));
    this.subSlide.selectedInsightIds = ids;
    this.subSlide.insights = this.buildStoredInsights(ids, byId);
    this.onUpdate();
  }

  onSlideTextInput(q: InsightQuestion, event: Event) {
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    if (!this.subSlide || !Array.isArray(this.subSlide.insights)) {
      return;
    }
    const insights = this.subSlide.insights.slice();
    const idx = insights.findIndex(
      (item: any) => item && typeof item !== 'string' && item.id === q.id,
    );
    if (idx < 0) {
      return;
    }
    const current = insights[idx];
    insights[idx] = {
      ...current,
      question: q.question,
      slideQuestion: value,
    };
    this.subSlide.insights = insights;
    this.onUpdate();
  }

  onUpdate() {
    this.$emit('update');
  }
}
